import { useState, useCallback } from 'react';
import { OptionType, CreateOptionTypeRequest } from '../../types/optionTypes';
import { useToast } from '../ui/useToast';

const initialFormData: CreateOptionTypeRequest = {
  name: '',
  display_name: '',
  options: [],
  default_value: '',
  sort_order: 0,
};

export const useOptionForm = () => {
  const [formData, setFormData] = useState<CreateOptionTypeRequest>(initialFormData);
  const [optionInput, setOptionInput] = useState('');
  const [editingOption, setEditingOption] = useState<OptionType | null>(null);
  const { showError } = useToast();

  // Reset form to initial state
  const resetForm = useCallback(() => {
    setFormData({ ...initialFormData });
    setOptionInput('');
    setEditingOption(null);
  }, []);

  // Set form for editing
  const setEditForm = useCallback((option: OptionType) => {
    setFormData({
      name: option.name,
      display_name: option.display_name,
      options: option.options,
      default_value: option.default_value,
      sort_order: option.sort_order,
    });
    setEditingOption(option);
  }, []);

  // Update form data
  const updateFormData = useCallback((updates: Partial<CreateOptionTypeRequest>) => {
    setFormData(prev => ({ ...prev, ...updates }));
  }, []);

  // Add option to form
  const addOption = useCallback(() => {
    const trimmedInput = optionInput.trim();
    if (trimmedInput && !formData.options.includes(trimmedInput)) {
      setFormData(prev => ({
        ...prev,
        options: [...prev.options, trimmedInput],
      }));
      setOptionInput('');
    }
  }, [optionInput, formData.options]);

  // Remove option from form
  const removeOption = useCallback((optionToRemove: string) => {
    setFormData(prev => ({
      ...prev,
      options: prev.options.filter(opt => opt !== optionToRemove),
      default_value: prev.default_value === optionToRemove ? '' : prev.default_value,
    }));
  }, []);

  // Validate form data
  const validateForm = useCallback((): boolean => {
    if (!formData.name || !formData.display_name || formData.options.length === 0) {
      showError('Lütfen tüm alanları doldurun');
      return false;
    }

    if (!formData.default_value) {
      showError('Lütfen varsayılan değer seçin');
      return false;
    }

    return true;
  }, [formData, showError]);

  // Check if form is valid
  const isFormValid: boolean = Boolean(
    formData.name && formData.display_name && formData.options.length > 0 && formData.default_value
  );

  return {
    // Form state
    formData,
    optionInput,
    editingOption,
    isFormValid,

    // Form actions
    resetForm,
    setEditForm,
    updateFormData,
    setOptionInput,
    addOption,
    removeOption,
    validateForm,
  };
};
